import React from 'react';
import { Link } from 'react-router-dom';
import Modal, { ModalTitle, ModalContent, ModalActions } from '../basic/Modal';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="4xl" maxHeight="80vh" aria-labelledby="terms-modal-title">
      <ModalTitle id="terms-modal-title">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Terms & Conditions</h2>
            <p className="text-sm text-gray-500 mt-1">Please read our terms of service carefully</p>
          </div>
        </div>
      </ModalTitle>

      <ModalContent className="px-0 py-0 overflow-hidden">
        <div className="h-full overflow-y-auto bg-gradient-to-r from-green-50 to-blue-50">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Last updated: August 2, 2025</span>
            </div>
          </div>

          <div className="px-6 py-6">
            <div className="text-gray-800 text-sm leading-relaxed max-w-none space-y-4">
              <div className="text-justify">
                <p className="mb-3">
                  Please read these Terms of Service ("<strong>Terms</strong>," or "<strong>Terms of Service</strong>")
                  carefully before using the STATDOG mobile application and/or any related web-based services (the "
                  <strong>Service</strong>") operated by <strong>Sports Data Partners, LLC</strong> ("
                  <strong>Sports Data</strong>," "<strong>us</strong>," "<strong>we</strong>," or "<strong>our</strong>
                  "), a Delaware limited liability company.
                </p>

                <p className="mb-3">
                  These Terms of Service constitute a legally binding agreement made between you, whether personally or
                  on behalf of an entity, and Sports Data concerning your access to and use of our Service. By accessing
                  and continuing your use of our Service, or by checking any authorizations boxes accompanying any links
                  or messages by which you arrived at these Terms, you agree to be bound by these Terms and all other
                  terms, such as our Privacy Policy, as updated from time to time and incorporated hereto by reference.
                  Your access to and use of the Service is conditioned upon your acceptance of and compliance with these
                  Terms. These Terms apply to all visitors, users, and others who access or use the Service.
                </p>

                <p className="mb-3">
                  By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part
                  of the Terms, then you may not access the Service.
                </p>

                <p className="mb-3">
                  We reserve the right to change or modify any of the Terms and/or our Service at any time. If we decide
                  to change our Terms, we will notify you by revising the date at the top of the Terms of Service and
                  you waive any right to receive specific notice of each such change. Your use of our Service following
                  the posting of changes or modifications to the Terms will constitute your acceptance of the revised
                  Terms. Therefore, you should review the Terms and applicable policies from time to time to understand
                  the terms that apply to your use of the Service. If you do not agree to the amended Terms, you must
                  immediately stop using the Service.
                </p>

                <p className="mb-3">
                  You must be at least thirteen (13) years old to use the Service and by accessing or using our Service,
                  you represent and warrant that you are at least thirteen (13) years old. If you are not 13 years old,
                  do not continue to use the Service.
                </p>

                <p className="mb-6">
                  If you are using the Service on behalf of another individual or entity (e.g., your school or players)
                  (each a "<strong>Beneficiary</strong>"), you represent, warrant, and agree: (1) that all Beneficiaries
                  are at least 13 years old; (2) that you have disclosed to each Beneficiary your use of our Services,
                  these Terms, and the terms of our Privacy Policy; (3) that you will truthfully and accurately disclose
                  the name and contact information for any Beneficiary necessary for us to accurately confer that
                  information to others; (4) that all materials submitted by you for each Beneficiary ("
                  <strong>Beneficiary Materials</strong>") comply with these Terms; and (5) that you shall indemnify and
                  defend us (a) from any third-party claim that Beneficiary Materials do not comply with these Terms or
                  otherwise violate any third-party's rights or applicable laws, and (b) from any claim brought by any
                  Beneficiary you used our Services on behalf of.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                1. About STATDOG
              </h3>

              <p className="text-justify mb-4">
                STATDOG is a sports science lacrosse statistics app designed to empower spectators and coaches to
                capture, report, view, accumulate, store, track, and analyze lacrosse player and team game data in
                real-time. Our goal is to provide coaches with instant insights into men's and boys' lacrosse
                performance.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                2. Accounts
              </h3>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">2.1. Your Account:</h4>
                <p className="mb-3">
                  While portions of the Service can be viewed without a user account, in order to access and utilize the
                  full Service, you will be required to create a profile or account (an "<strong>Account</strong>"),
                  select a membership option, and enter a valid payment method. When you create an Account with us, you
                  must provide information that is accurate, complete, and current at all times. Failure to do so
                  constitutes a breach of the Terms, which may result in the immediate termination of your account on
                  our Service. In the creation of your Account, you warrant that you are not impersonating any third
                  party or otherwise using any third party's information without their prior written approval. As
                  mentioned above, if you use your Account on behalf of a Beneficiary, you must provide the full legal
                  name of the Beneficiary related to the Beneficiary Materials.
                </p>

                <p className="mb-3">
                  If we have reason to believe that your Account information is untrue, inaccurate, out-of-date, or
                  incomplete, we reserve the right, in our sole discretion, to suspend or terminate your Account, and/or
                  suspend or terminate your use of the Service and to remove any information or content posted by your
                  Account. At all times, you shall keep your Account personal to you and not permit any unaffiliated
                  third party to access your Account or Account Information. You are responsible for safeguarding the
                  password that you use to access the Service and for any activities or actions conducted under your
                  Account.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">2.2. Account Security:</h4>
                <p className="mb-3">
                  You agree not to disclose your password to any third party. You must notify us immediately upon
                  becoming aware of any breach of security or unauthorized use of your Account. We will not be liable
                  for any loss you incur due to any unauthorized or improper use of your Account Information by you or
                  third parties. You, however, may be liable for any loss we or others incur caused by use of your
                  Account Information, whether caused by you, by an authorized person, or by an unauthorized person.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                3. User-Generated Content and Data Ownership
              </h3>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">3.1. Data Ownership:</h4>
                <p className="mb-3">
                  You, as the client establishing an account within the STATDOG Service, retain all ownership rights to
                  the player names, school name, team name, player statistics, player information, and team statistics
                  (collectively, "<strong>User-Generated Content</strong>" or "<strong>UGC</strong>") that you input,
                  capture, or generate within the STATDOG app. If you input, capture, or generate UGC on behalf of a
                  Beneficiary, you warrant that you have secured all necessary permissions and/or licenses to the
                  Beneficiary Materials that underlie any UGC. Sports Data does not claim any ownership rights over your
                  or your Beneficiary's UGC.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">3.2. License Grant:</h4>
                <p className="mb-3">
                  By inputting, capturing, or generating UGC within the STATDOG app, you grant Sports Data a perpetual,
                  non-exclusive, worldwide, royalty-free, transferable, sublicensable license to use, reproduce, modify,
                  adapt, publish, translate, create derivative works from, distribute, publicly perform, and publicly
                  display such UGC solely for the purpose of operating, improving, providing, and developing the STATDOG
                  Service or supporting and facilitating the business operations of Sports Data<strong>.</strong> This
                  includes, but is not limited to, displaying data within the app, performing statistical analysis to
                  enhance features, use within marketing or promotional materials, and ensuring the functionality of the
                  Service. This license is granted for the duration of your use of the Service and will terminate upon
                  the deletion of your account.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">3.3. Data Accuracy:</h4>
                <p className="mb-3">
                  You are solely responsible for the accuracy, completeness, and legality of all UGC you input into the
                  STATDOG app. Sports Data is not responsible for any errors or inaccuracies in the UGC.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">3.4. Data Privacy:</h4>
                <p className="mb-3">
                  While you own your UGC, Sports Data will handle all UGC in accordance with our Privacy Policy. We are
                  committed to protecting the privacy of our users and their data. Please review our Privacy Policy for
                  more information on how we collect, use, and disclose information, including UGC.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                4. Prohibited Uses
              </h3>

              <p className="text-justify mb-4">
                You may use the Service only for lawful purposes and in accordance with these Terms. You agree not to
                use the Service or induce, encourage or enable any third person to use the Service:
              </p>

              <ul className="ml-6 space-y-2 list-disc mb-4">
                <li className="text-gray-700">
                  In any way that violates any applicable national, state, local, or international laws or regulations.
                </li>
                <li className="text-gray-700">
                  For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way.
                </li>
                <li className="text-gray-700">
                  To transmit, or procure the sending of, any advertising or promotional material, including any "junk
                  mail," "chain letter," "spam," or any other similar solicitation.
                </li>
                <li className="text-gray-700">
                  To impersonate or attempt to impersonate Sports Data, a Sports Data employee, another user, or any
                  other person or entity.
                </li>
                <li className="text-gray-700">
                  To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or
                  which, as determined by us, may harm Sports Data or users of the Service or expose them to liability.
                </li>
                <li className="text-gray-700">
                  To directly engage in any defamatory, harassing, bullying, anti-social, or otherwise abusive conduct
                  directed at or in reference to any other person or entity.
                </li>
              </ul>

              <p className="text-justify mb-4">Additionally, you agree not to:</p>

              <ul className="ml-6 space-y-2 list-disc mb-4">
                <li className="text-gray-700">
                  Use the Service in any manner that could disable, overburden, damage, or impair the Service or
                  interfere with any other party's use of the Service, including their ability to engage in real-time
                  activities through the Service.
                </li>
                <li className="text-gray-700">
                  Use any robot, spider, or other automatic device, process, or means to access or utilize the Service
                  for any purpose, including monitoring or copying any of the material on the Service or the Service's
                  underlying programming and code.
                </li>
                <li className="text-gray-700">
                  Use any manual, digital, or automatic process to monitor or copy any of the material on the Service,
                  the Service itself, or for any other unauthorized purpose without our prior written consent.
                </li>
                <li className="text-gray-700">
                  Use any device, software, or routine that interferes with the proper working of the Service.
                </li>
                <li className="text-gray-700">
                  Introduce any viruses, Trojan horses, worms, logic bombs, or other material that is malicious or
                  technologically harmful.
                </li>
                <li className="text-gray-700">
                  Attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Service,
                  the server on which the Service is stored, or any server, computer, or database connected to the
                  Service.
                </li>
                <li className="text-gray-700">
                  Attack the Service via a denial-of-service attack or a distributed denial-of-service attack.
                </li>
                <li className="text-gray-700">
                  Otherwise attempt to interfere with the proper working of the Service.
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                5. Intellectual Property
              </h3>

              <p className="text-justify mb-4">
                The Service and its original content (excluding UGC), features, and functionality are and will remain
                the exclusive property of Sports Data and its licensors. The Service is protected by copyright,
                trademark, and other laws of both the United States and foreign countries. Our trademarks and trade
                dress may not be used in connection with any product or service without the prior written consent of
                Sports Data. You agree that you will not use the Service or your Account to infringe the intellectual
                property or other right of any person or entity or otherwise violate any privacy or publicity rights or
                impersonate any other person. You will not use the Service or your Account in a manner which infringes
                any duty of confidentiality that you owe to another person or entity.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                6. Reservation of Rights
              </h3>

              <p className="text-justify mb-4">
                We reserve the right to deny, cancel, terminate, suspend, lock, or modify access to the Service or
                control of any Account for any reason as determined by us in our sole discretion, including, but not
                limited to, the following:
              </p>

              <ul className="ml-6 space-y-2 list-disc mb-4">
                <li className="text-gray-700">
                  To correct mistakes made by us in offering or delivering any Services;
                </li>
                <li className="text-gray-700">To assist with our fraud and abuse detection and prevention efforts;</li>
                <li className="text-gray-700">
                  To comply with court orders against you or other users and applicable local, state, and federal laws,
                  rules and regulations;
                </li>
                <li className="text-gray-700">
                  To comply with requests of law enforcement, including subpoena requests;
                </li>
                <li className="text-gray-700">To comply with any dispute resolution process;</li>
                <li className="text-gray-700">
                  To defend any legal action or threatened legal action without consideration for whether such legal
                  action or threatened legal action is eventually determined to be with or without merit;
                </li>
                <li className="text-gray-700">
                  To avoid any civil or criminal liability on the part of Sports Data, its officers, directors,
                  employees, licensors or partners, including, but not limited to, instances where you have sued or
                  threatened to sue Sports Data or its licensors;
                </li>
                <li className="text-gray-700">
                  In the event you request to terminate or delete your Account or in the event your subscription or
                  membership terminates; or
                </li>
                <li className="text-gray-700">
                  If we have reason to believe that any Account is being used or intended to be used for a reason which
                  would violate these Terms or our Privacy Policy.
                </li>
              </ul>

              <p className="text-justify mb-4">
                We reserve the right (but undertake no duty) to monitor all user content and UGC and decide whether it
                is appropriate and/or complies with these Terms or our Privacy Policy. We may, with or without notice,
                in our sole discretion, remove or modify any content and/or terminate your access to the Service, your
                Account, or your membership for posting or publishing any material in violation of these Terms or our
                other policies.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                7. Links to Other Websites
              </h3>

              <p className="text-justify mb-4">
                Our Service may contain links to other third-party websites or services that are not owned, operated, or
                controlled by Sports Data. If you click on a third-party link, you may be directed to that third party's
                site. These third parties may use cookies alone or in conjunction with web beacons or other tracking
                technologies to collect information about you when you use our Service. The information they collect may
                be associated with your personal information or they may collect information, including personal
                information, about your online activities over time and across different websites and other online
                services. They may use this information to provide you with interest-based (behavioral) advertising or
                other targeted content. We do not control these third parties' tracking technologies or how they may be
                used.
              </p>

              <p className="text-justify mb-4">
                Sports Data has no control over and assumes no responsibility for, the content, privacy policies, or
                practices of any third-party websites or services. You further acknowledge and agree that Sports Data
                shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to
                be caused by or in connection with the use of or reliance on any such content, goods, or services
                available on or through any such websites or services.
              </p>

              <p className="text-justify mb-4">
                We strongly advise you to read the terms and conditions and privacy policies of any third-party websites
                or services that you visit. These Terms do not apply to any websites or services provided by any third
                party, including through any application or content that may link to or be accessible from or on our
                Service.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                8. Reliance on Information Posted
              </h3>

              <p className="text-justify mb-4">
                The information presented on or through the Service is made available solely for general information
                purposes. We do not warrant the accuracy, completeness, or usefulness of this information. Any reliance
                you place on such information is strictly at your own risk. We disclaim all liability and responsibility
                arising from any reliance placed on such materials by you or any other visitor to the Service.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                9. Disclaimer
              </h3>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-justify text-sm">
                  THE SERVICE, INCLUDING SPORTS DATA CONTENT, AND ALL LINKS, INFORMATION, MATERIALS, SERVICES, AND/OR
                  PRODUCTS PROVIDED ON OR THROUGH THE SERVICE ("<strong>SERVICE MATERIALS</strong>") ARE PROVIDED ON AN
                  "AS IS," "AS-AVAILABLE," AND "WITH ALL FAULTS" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
                  IMPLIED. YOU EXPRESSLY AGREE THAT YOUR USE OF SERVICE MATERIALS IS AT YOUR SOLE RISK. TO THE FULLEST
                  EXTENT PERMITTED BY LAW, SPORTS DATA AND OUR AFFILIATES, SUCCESSORS, OFFICERS, DIRECTORS,
                  SHAREHOLDERS, PERSONAL REPRESENTATIVES, AGENTS, AND EMPLOYEES (THE "
                  <strong>ASSOCIATED PARTIES</strong>") DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT
                  LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
                  NON-INFRINGEMENT AS TO SERVICE MATERIALS AND YOUR USE THEREOF. IN ADDITION, SPORTS DATA AND THE
                  ASSOCIATED PARTIES DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR THIRD-PARTY
                  PRODUCTS OR SERVICES, EVEN IF WE HAVE INTRODUCED YOU TO SUCH PARTY OR PARTIES, AND WE WILL NOT BE A
                  PARTY TO OR IN ANY WAY BE RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND ANY THIRD-PARTY
                  PROVIDERS OF THE THIRD-PARTY PRODUCTS OR SERVICES.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                10. Limitation Of Liability
              </h3>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                <p className="text-justify text-sm">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SPORTS DATA OR THE ASSOCIATED
                  PARTIES BE LIABLE TO YOU OR ANY THIRD-PARTY FOR ANY, DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY,
                  INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, OR ANY OTHER DAMAGES OF ANY KIND WHATSOEVER, INCLUDING BUT
                  NOT LIMITED TO LOSS OF USE, LOSS OF PROFITS OR REVENUE, OR LOSS OF DATA OR OTHER INFORMATION, FOR
                  BUSINESS INTERRUPTION, AND FOR ANY OTHER PECUNIARY OR OTHER LOSS WHATSOEVER, ARISING OUT OF OR IN ANY
                  WAY RELATED TO THE SERVICE, OUR ACTS OR OMISSIONS UNDER OR IN ANY WAY RELATED TO THE SERVICE, OR
                  OTHERWISE UNDER OR IN CONNECTION WITH THE SERVICE, REGARDLESS OF THE LEGAL THEORY, INCLUDING WITHOUT
                  LIMITATION TORT, STRICT LIABILITY, BREACH OF CONTRACT, OR BREACH OF WARRANTY BY US OR ANY ASSOCIATED
                  PARTY, AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
              </div>

              <p className="text-justify mb-4">
                THE FOREGOING LIMITATIONS, EXCLUSIONS AND DISCLAIMERS OF THIS "LIMITATION OF LIABILITY" SECTION SHALL
                APPLY TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EVEN IF ANY REMEDY FAILS ITS ESSENTIAL PURPOSE.
                NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN AND TO THE FULLEST EXTENT PERMITTED BY
                APPLICABLE LAW, IN NO EVENT SHALL THE AGGREGATE LIABILITY OF SPORTS DATA OR THE ASSOCIATED PARTIES TO
                YOU FOR ANY CAUSE WHATSOEVER AND REGARDLESS OF THE FORM OF THE ACTION, WHETHER IN CONTRACT, WARRANTY,
                TORT (INCLUDING NEGLIGENCE, WHETHER ACTIVE, PASSIVE, OR IMPUTED), OR OTHER THEORY, ARISING OUT OF OR
                RELATING TO THE USE OR INABILITY TO USE THE SITE/SERVICES EXCEED THE AMOUNT PAID, IF ANY, BY YOU TO US
                DURING THE THREE (3) MONTH PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                11. Indemnification
              </h3>

              <p className="text-justify mb-4">
                To the fullest extent permitted by applicable law, you agree to indemnify, defend, and hold Sports Data
                and the Associated Parties harmless from any loss, damage, liability, claim, or demand, including
                reasonable attorneys' fees and expenses, made by any third party due to or arising out of your: (1)
                access to the Service, by you or any person using your account and password; (2) use of the Service by
                your Account; (3) breach of these Terms or our Privacy Policy; (4) any breach of your representations
                and warranties set forth in these Terms or our Privacy Policy; (5) your violation of the rights of a
                third party, including but not limited to intellectual property rights; (6) use of the Service to commit
                any overt harmful act toward any other user of the Service; or (7) your UGC or UGC you post on behalf of
                a Beneficiary or the provision thereof. Notwithstanding the foregoing, we reserve the right, at your
                expense, to assume the exclusive defense and control of any matter for which you are required to
                indemnify us, and you agree to cooperate, at your expense, with our defense of such claims.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                12. Compliance with Local Laws
              </h3>

              <p className="text-justify mb-4">
                We make no representation or warranty that the content available on the Service is appropriate in every
                country, state, or jurisdiction, and access to the Service from countries or jurisdictions where its
                content is illegal is prohibited. If you choose to access the Service, you are responsible for
                compliance with all local laws, rules, and regulations and we shall not be liable in any way in the
                event the Service is not permitted in any jurisdiction.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                13. Governing Law
              </h3>

              <p className="text-justify mb-4">
                These Terms shall be governed and construed in accordance with the laws of the State of Florida, United
                States, without regard to its conflict of law provisions.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                14. Waiver
              </h3>

              <p className="text-justify mb-4">
                No waiver by us of any term or condition set out in these Terms shall be deemed a further or continuing
                waiver of such term or condition or a waiver of any other term or condition, and any failure by us to
                assert a right or provision under these Terms shall not constitute a waiver of such right or provision.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                15. Severability
              </h3>

              <p className="text-justify mb-4">
                Each covenant and agreement in these Terms shall be construed for all purposes to be a separate and
                independent covenant or agreement. If a court of competent jurisdiction holds any provision (or portion
                of a provision) of these Terms to be illegal, invalid, or otherwise unenforceable, the remaining
                provisions (or portions of provisions) of these Terms shall not be affected thereby and shall be found
                to be valid and enforceable to the fullest extent permitted by law.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                16. Entire Agreement
              </h3>

              <p className="text-justify mb-4">
                These Terms and the Privacy Policy referred to herein constitute the sole and entire agreement between
                you and Sports Data with respect to your Account, membership, and all access to and use of the Service
                and supersedes all prior and contemporaneous understandings, agreements, representations and warranties,
                both written and oral, with respect to the Service.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                17. Disputes
              </h3>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">17.1. Informal Negotiations:</h4>
                <p className="mb-3">
                  To expedite resolution and control the cost of any dispute, controversy, or claim related to these
                  Terms (each a "<strong>Dispute</strong>" and collectively, the "<strong>Disputes</strong>") brought by
                  either you or us (individually, a "<strong>Party</strong>" and collectively, the "
                  <strong>Parties</strong>"), the Parties agree to first attempt to negotiate any Dispute (except those
                  Disputes expressly provided below) informally for at least thirty (30) calendar days before initiating
                  arbitration. Such informal negotiations commence upon written notice from one Party to the other
                  Party.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">17.2. Binding Arbitration:</h4>
                <p className="mb-3">
                  If the Parties are unable to resolve a Dispute through informal negotiations, the Dispute (except
                  those Disputes expressly excluded below) will be finally and exclusively resolved by binding
                  arbitration. The arbitration shall be commenced and conducted under the Commercial Arbitration Rules
                  of the American Arbitration Association ("<strong>AAA</strong>
                  ") and, where appropriate, the AAA's Supplementary Procedures for Consumer Related Disputes ("
                  <strong>AAA Consumer Rules</strong>
                  "), both of which are available at the American Arbitration Association (AAA) website. If the claim
                  asserted in arbitration is for less than $75,000, the AAA's Consumer Arbitration Rules will apply. If
                  the claim asserted is for $75,000 or more, the Commercial Arbitration Rules will apply.
                </p>

                <p className="mb-3">
                  Sports Data may choose, at its sole discretion, to have the arbitration conducted by telephone or
                  videoconference or in Orlando, Florida. Any judgment on the award rendered by the arbitrator may be
                  entered in any state or federal court located in Orlando, Florida. Except to the extent preempted by
                  or inconsistent with applicable federal law and as otherwise described herein, this agreement is
                  governed by the laws of Florida without regard to choice of law principles, conflicts of laws rules,
                  or your actual state of residence. Except as otherwise provided herein, the Parties may litigate in
                  court to compel arbitration, stay proceedings pending arbitration, or to confirm, modify, vacate, or
                  enter judgment on the award entered by the arbitrator.
                </p>

                <p className="mb-3">
                  <span className="underline">Restrictions</span>. The Parties agree that any arbitration shall be
                  limited to the Dispute between the Parties individually. To the full extent permitted by law, (a) no
                  arbitration shall be joined with any other proceeding; (b) there is no right or authority for any
                  Dispute to be arbitrated on a class-action basis or to utilize class action procedures; and (c) there
                  is no right or authority for any Dispute to be brought in a purported representative capacity on
                  behalf of the general public or any other persons.
                </p>

                <p className="mb-3">
                  <span className="underline">Exceptions to Informal Negotiations and Arbitration</span>. The Parties
                  agree that the following Disputes arising from a user's conduct are not subject to the above
                  provisions concerning informal negotiations and binding arbitration: (a) any action by Sports Data to
                  enforce any of its intellectual property rights; (b) any action by Sports Data related to, or arising
                  from, allegations of theft, piracy, invasion of privacy, or unauthorized use; and (c) any Sports Data
                  claim for injunctive relief.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">17.3. Class Action Waiver:</h4>
                <p className="mb-3">
                  You waive the right to serve as a class representative, class member, or otherwise participate in any
                  class, collective, consolidated, or representative proceeding against us related to any disputes
                  arising under these Terms. You agree that this class action waiver is material and essential to the
                  resolution of any dispute and is non-severable from these Terms and your use of the Service.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                18. No Joint Venture/Partnership
              </h3>

              <p className="text-justify mb-4">
                The relationship established by these Terms is that of independent contractors and nothing contained in
                these Terms shall be construed to: (i) give either party the power to direct and control the business
                activities of the other; (ii) constitute the parties as partners, joint venturers, agents,
                franchisor/franchisee, or otherwise as participants in a joint or common undertaking; or (iii) allow
                either party to create or assume any obligation on behalf of the other party.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                19. Electronic Communications
              </h3>

              <p className="text-justify mb-4">
                Whenever you use the Service, contact us through our website, or send us email or mail, you are
                communicating with us electronically. For that reason, you also consent to receive communications from
                us electronically. We will communicate with you by email at the email address which you provide in your
                Account Information. You agree that all agreements, notices, disclosures, and other communications that
                we provide to you electronically satisfy any legal requirement that such communications be in writing,
                to the extent permitted by applicable law.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                20. Successors And Assigns
              </h3>

              <p className="text-justify mb-4">
                These Terms shall be binding upon and inure to the benefit of the parties hereto and their respective
                heirs, successors and assigns.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                21. Contact Us
              </h3>

              <p className="text-justify mb-4">If you have any questions about these Terms, please contact us:</p>

              <ul className="ml-6 space-y-2 list-disc">
                <li className="text-gray-700">
                  By email:{' '}
                  <a href="mailto:support@statdog.live" className="underline">
                    support@statdog.live
                  </a>
                </li>
                <li className="text-gray-700">
                  By visiting our{' '}
                  <Link to="/contact-us" className="text-green-600 hover:text-green-700 underline font-medium">
                    Contact Us page
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ModalContent>

      <ModalActions className="bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span>Sports Data Partners, LLC</span>
          </div>
          <button
            onClick={onClose}
            className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
          >
            I Understand
          </button>
        </div>
      </ModalActions>
    </Modal>
  );
};

export default TermsAndConditionsModal;
